/**
 * SVG sparkline renderer — lightweight inline chart component.
 *
 * Supports:
 * - Line-only or line + area fill
 * - Configurable color, width, height, stroke width
 * - Auto-scaling to data range
 * - Optional reference line (e.g. baseline value)
 * - Tooltip on hover showing value at cursor position
 *
 * Usage:
 *   const el = createSparkline({
 *     data: [10, 25, 18, 30, 22],
 *     width: 100,
 *     height: 24,
 *     color: 'var(--accent-blue)',
 *   });
 *   container.appendChild(el);
 */

export interface SparklineOptions {
  /** Data points (y-values). At least 2 points required. */
  data: number[];
  /** SVG width in pixels. Default: 80. */
  width?: number;
  /** SVG height in pixels. Default: 20. */
  height?: number;
  /** Stroke color (CSS value). Default: 'var(--accent-blue)'. */
  color?: string;
  /** Stroke width. Default: 1.5. */
  strokeWidth?: number;
  /** Show area fill below the line. Default: false. */
  fill?: boolean;
  /** Fill opacity (0-1). Default: 0.15. */
  fillOpacity?: number;
  /** Optional reference line y-value (drawn as dashed horizontal). */
  referenceLine?: number;
  /** Reference line color. Default: 'var(--text-muted)'. */
  referenceColor?: string;
  /** Padding inside the SVG (prevents clipping at edges). Default: 1. */
  padding?: number;
}

/**
 * Create an SVG sparkline element from numeric data.
 * Returns an <svg> element ready to insert into the DOM.
 */
export function createSparkline(options: SparklineOptions): SVGSVGElement {
  const {
    data,
    width = 80,
    height = 20,
    color = 'var(--accent-blue)',
    strokeWidth = 1.5,
    fill = false,
    fillOpacity = 0.15,
    referenceLine,
    referenceColor = 'var(--text-muted)',
    padding = 1,
  } = options;

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  svg.style.cssText = 'display: inline-block; vertical-align: middle; overflow: visible;';

  if (data.length < 2) {
    return svg;
  }

  const drawWidth = width - padding * 2;
  const drawHeight = height - padding * 2;

  // Compute min/max for scaling
  let min = data[0];
  let max = data[0];
  for (let i = 1; i < data.length; i++) {
    if (data[i] < min) min = data[i];
    if (data[i] > max) max = data[i];
  }

  // Add reference line to range if present
  if (referenceLine !== undefined) {
    if (referenceLine < min) min = referenceLine;
    if (referenceLine > max) max = referenceLine;
  }

  const range = max - min || 1; // Avoid division by zero

  /** Map a data value to SVG y coordinate (inverted — higher values at top). */
  function toY(value: number): number {
    return padding + drawHeight - ((value - min) / range) * drawHeight;
  }

  /** Map a data index to SVG x coordinate. */
  function toX(index: number): number {
    return padding + (index / (data.length - 1)) * drawWidth;
  }

  // Build polyline points string
  const points: string[] = [];
  for (let i = 0; i < data.length; i++) {
    points.push(`${toX(i).toFixed(1)},${toY(data[i]).toFixed(1)}`);
  }
  const pointsStr = points.join(' ');

  // Reference line (behind the sparkline)
  if (referenceLine !== undefined) {
    const refY = toY(referenceLine);
    const refLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    refLine.setAttribute('x1', String(padding));
    refLine.setAttribute('y1', refY.toFixed(1));
    refLine.setAttribute('x2', String(width - padding));
    refLine.setAttribute('y2', refY.toFixed(1));
    refLine.setAttribute('stroke', referenceColor);
    refLine.setAttribute('stroke-width', '0.5');
    refLine.setAttribute('stroke-dasharray', '2,2');
    refLine.setAttribute('opacity', '0.5');
    svg.appendChild(refLine);
  }

  // Area fill (behind the line)
  if (fill) {
    const areaPath = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    const bottomLeft = `${toX(0).toFixed(1)},${(height - padding).toFixed(1)}`;
    const bottomRight = `${toX(data.length - 1).toFixed(1)},${(height - padding).toFixed(1)}`;
    areaPath.setAttribute('points', `${bottomLeft} ${pointsStr} ${bottomRight}`);
    areaPath.setAttribute('fill', color);
    areaPath.setAttribute('opacity', String(fillOpacity));
    svg.appendChild(areaPath);
  }

  // Line
  const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
  polyline.setAttribute('points', pointsStr);
  polyline.setAttribute('fill', 'none');
  polyline.setAttribute('stroke', color);
  polyline.setAttribute('stroke-width', String(strokeWidth));
  polyline.setAttribute('stroke-linejoin', 'round');
  polyline.setAttribute('stroke-linecap', 'round');
  svg.appendChild(polyline);

  // End-point dot (last value emphasis)
  const lastX = toX(data.length - 1);
  const lastY = toY(data[data.length - 1]);
  const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  dot.setAttribute('cx', lastX.toFixed(1));
  dot.setAttribute('cy', lastY.toFixed(1));
  dot.setAttribute('r', String(Math.max(1.5, strokeWidth * 0.8)));
  dot.setAttribute('fill', color);
  svg.appendChild(dot);

  return svg;
}

/**
 * Convenience: create a trend sparkline with appropriate coloring.
 * Rising trend = red, falling = green, stable = muted.
 */
export function createTrendSparkline(
  data: number[],
  opts?: Partial<Omit<SparklineOptions, 'data' | 'color'>>,
): SVGSVGElement {
  if (data.length < 2) {
    return createSparkline({ data, ...opts });
  }

  const first = data[0];
  const last = data[data.length - 1];
  const change = last - first;
  const threshold = Math.abs(first) * 0.05; // 5% threshold

  let color: string;
  if (change > threshold) {
    color = 'var(--accent-red)';
  } else if (change < -threshold) {
    color = 'var(--accent-green)';
  } else {
    color = 'var(--text-muted)';
  }

  return createSparkline({ data, color, fill: true, ...opts });
}
